import React, { useCallback, useContext, useState } from 'react'
import { Button } from '@/components/ui/button'
import { TenantFeature } from '@/utils/Tenant'
import { GridColumn as Column, Grid, GridCellProps } from '@progress/kendo-react-grid'
import { ClientDetailedDto } from 'api/Clients/Types/ClientDetailedDto'
import { TestAnswerDto, TestDto } from 'api/Clients/Types/TestDto'
import {
  clientTestTypes,
  getTestSubcategoryByName,
  TestSubcategory,
} from 'api/Clients/Types/TestQuestionDto'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { FlexBox } from 'components/Box'
import { Modal } from 'components/CustomModal'
import MoreButton from 'components/MoreButton/MoreButton'
import { useApiClient } from 'utils/api/ApiClient'
import { ClientsApiClient } from 'utils/api/clientsApi'
import { useFetchOne } from 'utils/api/useFetch'
import { AuthSessionContext } from 'utils/AuthContext'
import { formatLocalDateUTC } from 'utils/date'
import { handleOpenWindow } from 'utils/openTestWindow'
import { hasPermission } from 'utils/permissionsValidator'
import { TableWithExport } from 'utils/table/withExport'

import { ClientDetailsNoMatch } from './NotFoundTest'

interface Props {
  data: ClientDetailedDto
}

const initialStateModal = {
  data: null,
  allowEdit: false,
  visible: false,
}

const ClientKYCTestPage: React.FC<Props> = ({ data }) => {
  const [auth] = useContext(AuthSessionContext)
  const clientsApiClient = useApiClient(ClientsApiClient)

  const [noMatchTestModal, setNoMatchTestModal] = useState<{
    data: TestAnswerDto | null
    allowEdit: boolean
    visible: boolean
  }>(initialStateModal)

  const clientTests = useCallback(() => clientsApiClient.getClientKYCTests(data.id), [data.id]) // eslint-disable-line react-hooks/exhaustive-deps
  const { data: tests } = useFetchOne(clientTests)

  const permissions = auth?.permissions || []

  const handleNoMatchTestModalClose = () => {
    setNoMatchTestModal(initialStateModal)
  }

  const handleOpenFilledTest = ({ id, test }: TestDto) => {
    handleOpenWindow({
      urlKey: TenantFeature.clientTestFilledWindow,
      pathParams: { testId: id ?? '', clientId: data.id, type: clientTestTypes.kyc },
      additionalParams: { subcategoryId: getTestSubcategoryByName(test.subcategory).toString() },
    })
  }

  const handleEditTestForm = ({ id, test }: TestDto) => {
    handleOpenWindow({
      permissions,
      permission: PermissionsEnum.CanAddClientKycUpdateTests,
      urlKey: TenantFeature.clientEditTestFormWindow,
      pathParams: { type: clientTestTypes.kyc, clientId: data.id ? data.id : '', testId: id ?? '' },
      additionalParams: { subcategoryId: getTestSubcategoryByName(test.subcategory).toString() },
    })
  }

  const handleAddNewTest = ({ subcategoryId }: { subcategoryId: number }) => {
    handleOpenWindow({
      permissions,
      permission: PermissionsEnum.CanAddClientKycUpdateTests,
      urlKey: TenantFeature.clientNewTestFormWindow,
      pathParams: { type: clientTestTypes.kyc, clientId: data.id ? data.id : '' },
      additionalParams: { subcategoryId: subcategoryId.toString() },
    })
  }

  return (
    <React.Fragment>
      {noMatchTestModal.visible && (
        <Modal visible={noMatchTestModal.visible} onClose={handleNoMatchTestModalClose}>
          <ClientDetailsNoMatch
            header='For this entity test cannot be added.'
            onClick={handleNoMatchTestModalClose}
          />
        </Modal>
      )}
      <FlexBox style={{ marginBottom: 36 }} alignItems='center' justifyContent='flex-start'>
        <Button
          disabled={!hasPermission(permissions, PermissionsEnum.CanAddClientKycUpdateTests)}
          onClick={() => handleAddNewTest({ subcategoryId: TestSubcategory.Individual })}
        >
          Add new
        </Button>
      </FlexBox>
      <TableWithExport
        data={tests || []}
        hasPermissions={hasPermission(permissions, PermissionsEnum.CanExportClientsModule)}
      >
        <Grid data={tests || []} resizable>
          <Column
            field='test.name'
            title='Name'
            className='table-columns__content'
            cell={(gridCellProps: GridCellProps) => (
              <td
                className='table-columns__link'
                onClick={() => handleOpenFilledTest(gridCellProps.dataItem)}
              >
                {gridCellProps.dataItem.test.name}
              </td>
            )}
          />
          <Column title='Version' field='test.version' className='table-columns__content' />
          <Column
            title='User'
            field='user.name'
            className='table-columns__content'
            cell={(gridCellProps: GridCellProps) => (
              <td className='table-columns__content'>{gridCellProps.dataItem.user?.name}</td>
            )}
          />
          <Column title='Type' field='test.category' className='table-columns__content' />
          <Column title='Score' field='totalScore' className='table-columns__content' />
          <Column title='Outcome' field='outcome.name' className='table-columns__content' />
          <Column
            field='dateTaken'
            title='Date Submitted'
            cell={(gridCellProps: GridCellProps) => (
              <td className='table-columns__date'>
                {formatLocalDateUTC(gridCellProps.dataItem.dateTaken, true)}
              </td>
            )}
          />
          {hasPermission(permissions, PermissionsEnum.CanAddClientKycUpdateTests) && (
            <Column
              width={50}
              title=''
              cell={(gridCellProps: GridCellProps) => (
                <td>
                  <MoreButton
                    items={[
                      {
                        name: 'Edit',
                        'data-test': 'edit',
                        onClick: () => handleEditTestForm(gridCellProps.dataItem),
                      },
                    ]}
                    data-test='more-button'
                  />
                </td>
              )}
            />
          )}
        </Grid>
      </TableWithExport>
    </React.Fragment>
  )
}

export default ClientKYCTestPage
